import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import { Platform, Pressable, StyleSheet, type ViewStyle } from 'react-native';
import { DatePicker as WheelDatePicker } from 'react-native-wheel-pick';

import type { AvailableIconName } from '@/assets/icons';
import { Stack } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { SvgIcon } from '@/components/ui/svg-icon';
import { Text } from '@/components/ui/text';
import type { TextInputStatus } from '@/components/ui/text-input/types';
import { WheelPickerSheet } from '@/components/ui/wheel-picker-sheet';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import type { TypographyToken } from '@/theme/typography';
import { DatePickerSelectFields } from './date-picker-select-fields';

type WheelDatePickerProps = {
  style?: ViewStyle;
  date?: Date;
  minimumDate?: Date;
  maximumDate?: Date;
  onDateChange?: (date: Date) => void;
  /** iOS: drives the spinner column order via `@react-native-community/datetimepicker`. */
  locale?: string;
  /** Android: explicit wheel column order for `react-native-wheel-pick`. */
  order?: string;
  /** Android: hide the native full-width selection band/lines (forwarded to each column). */
  isShowSelectBackground?: boolean;
  isShowSelectLine?: boolean;
  /** Wheel item font size (defaults to the lib's 26, which overflows long month names). */
  textSize?: number;
};

const DatePickerComponent = WheelDatePicker as unknown as FC<WheelDatePickerProps>;

export type DateTimePickerProps = {
  value?: Date | null;
  defaultValue?: Date | null;
  onDateChange?: (date: Date) => void;
  minimumDate?: Date;
  maximumDate?: Date;
  placeholder?: string;
  disabled?: boolean;
  status?: Exclude<TextInputStatus, 'disabled'>;
  helperText?: ReactNode;
  style?: ViewStyle | ViewStyle[];
  triggerStyle?: ViewStyle | ViewStyle[];
  /** Extra style applied on top of the default trigger look while disabled. */
  disableStyle?: ViewStyle | ViewStyle[];
  displayVariant?: TypographyToken;
  displayFormat?: (date: Date) => string;
  /**
   * How the wheel is presented when the trigger is pressed:
   * - `'modal'` (default): centered modal with Confirm / Cancel buttons.
   * - `'sheet'`: a bottom-sheet wheel with an `X / title / ✓` header.
   */
  presentation?: 'modal' | 'sheet';
  /** Title shown in the sheet header (only used when `presentation="sheet"`). */
  title?: string;
  /** Trigger icon. Defaults to `'IcCalendar'`. */
  iconName?: AvailableIconName;
  /** Trigger icon size in design px (auto-scaled). Defaults to `16`. */
  iconSize?: number;
  /** Trigger icon color. Defaults to the brand blue. */
  iconColor?: string;
  /** Hide the trigger icon entirely. */
  hideIcon?: boolean;
};

const formatDisplayDate = (date: Date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

export const DateTimePicker: FC<DateTimePickerProps> = memo(
  ({
    value,
    defaultValue = null,
    onDateChange,
    minimumDate = new Date('1875-01-01'),
    maximumDate,
    placeholder = 'Select date',
    disabled = false,
    status = 'default',
    helperText,
    style,
    triggerStyle,
    disableStyle,
    displayVariant = 'Font Inter/Subtitle/Regular 14px',
    displayFormat = formatDisplayDate,
    presentation = 'modal',
    title,
    iconName = 'IcCalendar',
    iconSize = 16,
    iconColor = colors.Blue,
    hideIcon = false,
  }) => {
    const [open, setOpen] = useState(false);
    const [pickerSession, setPickerSession] = useState(0);
    const [internalDate, setInternalDate] = useState<Date | null>(defaultValue);
    const styles = useStyles();
    const { scale, deviceWidth, isTablet } = useScaling();
    const changeDate = useRef<Date | null>(value ?? internalDate);

    const isControlled = value !== undefined;
    const selectedDate = isControlled ? (value ?? null) : internalDate;

    const displayLabel = useMemo(
      () => (selectedDate ? displayFormat(selectedDate) : placeholder),
      [displayFormat, placeholder, selectedDate],
    );
    const isPlaceholder = selectedDate == null;

    const handleDateChange = useCallback(
      (next: Date) => {
        if (!(next instanceof Date) || Number.isNaN(next.getTime())) return;

        // Some pickers return calendar day as 00:00:00.000Z. Local getters then shift the calendar
        // day west of UTC (e.g. America/Anchorage). Use UTC components only for that encoding.
        const isUtcMidnight =
          next.getUTCHours() === 0 &&
          next.getUTCMinutes() === 0 &&
          next.getUTCSeconds() === 0 &&
          next.getUTCMilliseconds() === 0;

        const y = isUtcMidnight ? next.getUTCFullYear() : next.getFullYear();
        const m = isUtcMidnight ? next.getUTCMonth() : next.getMonth();
        const d = isUtcMidnight ? next.getUTCDate() : next.getDate();

        let normalizedNextDate = new Date(y, m, d);
        if (Number.isNaN(normalizedNextDate.getTime())) return;

        const offsetMinutes = normalizedNextDate.getTimezoneOffset();
        if (offsetMinutes <= 0) {
          normalizedNextDate = new Date(
            normalizedNextDate.getTime() + offsetMinutes * 60 * 1000 * -1,
          );
        }

        if (!isControlled) setInternalDate(normalizedNextDate);
        changeDate.current = normalizedNextDate;
      },
      [isControlled],
    );

    const onConfirm = useCallback(() => {
      // The wheel always shows a date (the committed value, or today as the spinner's default).
      // If the user confirms WITHOUT scrolling, `changeDate.current` is still null because the
      // wheel never fired `onDateChange` — so fall back to what's displayed instead of no-op'ing
      // (which left the sheet stuck open). Clamp to min/max to match what the wheel can show.
      let next = changeDate.current ?? selectedDate ?? new Date();
      if (maximumDate && next.getTime() > maximumDate.getTime()) next = maximumDate;
      if (minimumDate && next.getTime() < minimumDate.getTime()) next = minimumDate;
      onDateChange?.(next);
      setOpen(false);
    }, [onDateChange, selectedDate, maximumDate, minimumDate]);

    const toggleOpen = () => {
      if (disabled) return;
      setOpen(prev => {
        const next = !prev;
        if (next) setPickerSession(s => s + 1);
        return next;
      });
    };

    useEffect(() => {
      if (selectedDate) {
        changeDate.current = selectedDate;
      }
    }, [selectedDate]);

    const pickerBody =
      Platform.OS === 'web' ? (
        <DatePickerSelectFields
          key={pickerSession}
          date={selectedDate ?? undefined}
          minimumDate={minimumDate}
          maximumDate={maximumDate ?? new Date('2199-12-31')}
          onDateChange={handleDateChange}
        />
      ) : (
        <DatePickerComponent
          key={pickerSession}
          style={presentation === 'sheet' ? styles.wheelSheet : styles.wheel}
          date={selectedDate ?? undefined}
          minimumDate={minimumDate}
          maximumDate={maximumDate ?? new Date('2199-12-31')}
          onDateChange={handleDateChange}
          // Force month-before-day (US) so every wheel matches the MM/DD/YYYY display and the
          // web fallback. iOS reads `locale`; Android reads `order`.
          locale="en-US"
          order="M-D-Y"
          // Smaller than the lib's default 26 so long month names ("September") don't overflow.
          textSize={scale(20)}
          // Android: hide the native band/lines so the sheet's rounded inset pill is the only
          // selection highlight (forwarded to each column via the lib's prop spread). iOS ignores.
          isShowSelectBackground={false}
          isShowSelectLine={false}
        />
      );

    return (
      <Stack style={[styles.root, style]}>
        {open && presentation === 'modal' ? (
          <Pressable style={styles.backdrop} onPress={() => setOpen(false)} />
        ) : null}

        <Pressable
          accessibilityRole="button"
          accessibilityState={{ expanded: open, disabled }}
          disabled={disabled}
          onPress={toggleOpen}
          style={[
            styles.trigger,
            status === 'error' ? styles.triggerError : undefined,
            disabled ? styles.disabled : undefined,
            triggerStyle,
            disabled ? disableStyle : undefined,
          ]}>
          <Text
            variant={displayVariant}
            style={[
              styles.triggerLabel,
              isPlaceholder ? styles.triggerPlaceholderLabel : undefined,
            ]}
            numberOfLines={1}>
            {displayLabel}
          </Text>

          {hideIcon ? null : (
            <Stack style={styles.iconWrapper}>
              <SvgIcon name={iconName} size={scale(iconSize)} color={iconColor} />
            </Stack>
          )}
        </Pressable>

        {presentation === 'sheet' ? (
          <WheelPickerSheet
            open={open}
            onOpenChange={setOpen}
            title={title}
            onConfirm={onConfirm}
            showSelectionPill>
            {pickerBody}
          </WheelPickerSheet>
        ) : open ? (
          <Modal visible={open} onBackdropPress={() => setOpen(false)}>
            <Stack
              padding={scale(12)}
              borderRadius={scale(12)}
              minWidth={Platform.select({
                android: !isTablet ? deviceWidth - scale(24) : scale(380),
                ios: undefined,
                web: !isTablet ? Math.min(deviceWidth - scale(24), scale(420)) : scale(420),
                default: scale(320),
              })}
              backgroundColor={'white'}>
              {pickerBody}
              <Stack direction="row" justify="flex-end" gap={scale(12)}>
                <Button variant="ghost" onPress={onConfirm}>
                  <Button.Text>Confirm</Button.Text>
                </Button>
                <Button variant="ghost" onPress={() => setOpen(false)}>
                  <Button.Text style={{ color: colors.Error }}>Cancel</Button.Text>
                </Button>
              </Stack>
            </Stack>
          </Modal>
        ) : null}
        {helperText ? (
          <Text
            variant="Font Inter/Subtitle/Regular 11px"
            style={[styles.helperText, status === 'error' ? styles.helperTextError : undefined]}>
            {helperText}
          </Text>
        ) : null}
      </Stack>
    );
  },
);

DateTimePicker.displayName = 'DateTimePicker';

const useStyles = createStyles(({ scale }) => ({
  root: {
    position: 'relative',
    zIndex: 20,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'transparent',
    zIndex: 1,
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: scale(1),
    borderRadius: scale(4),
    borderColor: colors['5'],
    backgroundColor: colors['Backgrounds/Primary'],
    paddingHorizontal: scale(12),
    height: scale({
      phone: 44,
      tablet: 48,
    }),
    zIndex: 2,
  },
  triggerError: {
    borderColor: colors.Error,
  },
  triggerLabel: {
    flex: 1,
    color: colors['Labels/Primary'],
    marginRight: scale(12),
  },
  triggerPlaceholderLabel: {
    color: colors['5'],
  },
  iconWrapper: {
    width: scale(20),
    height: scale(20),
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentWrapper: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: scale(8),
    zIndex: 3,
    backgroundColor: colors['Backgrounds/Primary'],
    borderRadius: scale(4),
    borderWidth: scale(1),
    borderColor: colors['5'],
    overflow: 'hidden',
    paddingHorizontal: scale(12),
    paddingVertical: scale(8),
  },
  disabled: {
    opacity: 0.6,
  },
  helperText: {
    marginTop: scale(6),
    color: colors['Labels/Secondary'],
  },
  helperTextError: {
    color: colors.Error,
  },
  wheel: {
    width: '100%',
    height: scale(220),
    backgroundColor: colors['Backgrounds/Primary'],
  },
  // Sheet variant: the iOS spinner (@react-native-community/datetimepicker) left-aligns its
  // wheels inside a frame wider than their content, leaving a gap. Let it use its intrinsic
  // width and center it instead. Android uses a different native picker that fills width.
  wheelSheet: {
    ...Platform.select({
      // Narrower than full width so the month/day/year columns (each flex:1) sit closer together
      // instead of spreading edge to edge. Centered via the sheet's `wheelWrap` alignItems.
      android: { width: '80%' },
      default: { alignSelf: 'center' },
    }),
    height: scale(220),
    // Transparent so the sheet's rounded selection pill (rendered behind) shows through on Android.
    backgroundColor: 'transparent',
  },
}));
